import type { RefObject } from "react"
import { useCallback, useEffect, useRef } from "react"

import type {
  CompanyProductAssociationListItem,
  CustomerProductAssociationsFilters,
} from "../stores/useCustomerProductAssociationsStore"
import { useCustomerProductAssociationsStore } from "../stores/useCustomerProductAssociationsStore"

import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"

interface UseCustomerProductListReturn {
  data: CompanyProductAssociationListItem[]
  isFetching: boolean
  tableContainerRef: RefObject<HTMLDivElement>
  hasItems: boolean
  handleScroll: () => void
  refetch: () => void
}

export const useCustomerProductList = (filters: CustomerProductAssociationsFilters): UseCustomerProductListReturn => {
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const data = useCustomerProductAssociationsStore(({ data }) => data)
  const fetching = useCustomerProductAssociationsStore(({ fetching }) => fetching)
  const executeSearch = useCustomerProductAssociationsStore(({ executeSearch }) => executeSearch)
  const loadMore = useCustomerProductAssociationsStore(({ loadMore }) => loadMore)
  const clearStore = useCustomerProductAssociationsStore(({ clearStore }) => clearStore)

  useEffect(() => () => clearStore(), [clearStore])

  useEffect(() => {
    executeSearch(queryClient, filters)
  }, [executeSearch, filters])

  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, filters), 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const hasItems = data.length > 0

  const refetch = useCallback(() => {
    executeSearch(queryClient, filters, true)
  }, [executeSearch, filters])

  return {
    data,
    isFetching: fetching,
    tableContainerRef,
    hasItems,
    handleScroll,
    refetch,
  }
}
